import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiUser, FiMail, FiPhone, FiMapPin, FiBook, FiSend, FiX } = FiIcons;

const CandidateRating = ({ candidate, onClose, onSubmit }) => {
  const [ratings, setRatings] = useState({
    englishLevel: 0,
    appearance: 0,
    technicalSkills: 0,
    communicationSkills: 0
  });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({ ...prev, [category]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    const evaluationData = {
      candidateId: candidate.id,
      companyId: candidate.companyId || 'current',
      ratings,
      notes,
      timestamp: new Date().toISOString()
    };
    
    await onSubmit(evaluationData);
    setLoading(false);
  };

  const renderStars = (category, currentValue) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleRatingChange(category, star)}
            className="text-2xl transition-colors"
          >
            <SafeIcon
              icon={FiStar}
              className={star <= currentValue ? 'text-yellow-400 fill-current' : 'text-gray-300'}
            />
          </button>
        ))}
      </div>
    );
  };

  const ratingCategories = [
    { key: 'englishLevel', label: 'English Level', description: 'Proficiency in English communication' },
    { key: 'appearance', label: 'Professional Appearance', description: 'Professional presentation and grooming' },
    { key: 'technicalSkills', label: 'Technical Skills', description: 'Technical knowledge and expertise' },
    { key: 'communicationSkills', label: 'Communication Skills', description: 'Clarity and effectiveness in communication' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">Rate Candidate</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex overflow-hidden" style={{ height: 'calc(90vh - 200px)' }}>
          {/* Candidate Profile Section */}
          <div className="w-2/5 border-r border-gray-200 p-6 overflow-y-auto bg-gray-50">
            <h4 className="font-semibold text-gray-900 mb-4">Candidate Profile</h4>
            
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="text-blue-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900">{candidate.name}</h5>
                    <p className="text-sm text-gray-600">{candidate.field || 'Not specified'}</p>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  {candidate.email && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <SafeIcon icon={FiMail} className="text-gray-400" />
                      <span>{candidate.email}</span>
                    </div>
                  )}
                  {candidate.phone && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <SafeIcon icon={FiPhone} className="text-gray-400" />
                      <span>{candidate.phone}</span>
                    </div>
                  )}
                  {candidate.city && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <SafeIcon icon={FiMapPin} className="text-gray-400" />
                      <span>{candidate.city}</span>
                    </div>
                  )}
                </div>
              </div>

              {candidate.skills && (
                <div className="bg-white p-4 rounded-lg">
                  <h6 className="font-medium text-gray-900 mb-2">Skills</h6>
                  <p className="text-sm text-gray-600">{candidate.skills}</p>
                </div>
              )}

              {candidate.experience && (
                <div className="bg-white p-4 rounded-lg">
                  <h6 className="font-medium text-gray-900 mb-2">Experience</h6>
                  <p className="text-sm text-gray-600">{candidate.experience}</p>
                </div>
              )}

              {candidate.education && (
                <div className="bg-white p-4 rounded-lg">
                  <h6 className="font-medium text-gray-900 mb-2">Education</h6>
                  <p className="text-sm text-gray-600">{candidate.education}</p>
                </div>
              )}
            </div>
          </div>

          {/* Rating Section */}
          <div className="flex-1 p-6 overflow-y-auto">
            <h4 className="font-semibold text-gray-900 mb-6">Evaluation Criteria</h4>
            
            <div className="space-y-6">
              {ratingCategories.map((category) => (
                <motion.div
                  key={category.key}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: ratingCategories.indexOf(category) * 0.1 }}
                  className="bg-gray-50 p-4 rounded-lg"
                >
                  <div className="mb-3">
                    <h5 className="font-medium text-gray-900">{category.label}</h5>
                    <p className="text-sm text-gray-600">{category.description}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Rating:</span>
                    {renderStars(category.key, ratings[category.key])}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-400">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </motion.div>
              ))}

              {/* Notes Section */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gray-50 p-4 rounded-lg"
              >
                <h5 className="font-medium text-gray-900 mb-3">Additional Notes</h5>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                  placeholder="Enter any additional observations or comments about this candidate..."
                />
              </motion.div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Overall Rating: <span className="font-semibold">
                {Object.values(ratings).reduce((sum, rating) => sum + rating, 0) / 4 || 0}/5
              </span>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || Object.values(ratings).some(rating => rating === 0)}
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} />
                    <span>Submit Rating</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CandidateRating;